import * as React from 'react';
import styled, { css } from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { IconButton } from '@creditornot/cb-button';
import { legacyToolsTypographyCss, typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { AccessabilityProps } from './AccessabilityProps';

type IconButtonProps = React.ComponentProps<typeof IconButton>;

const styleParams: IconButtonProps['styleParams'] = {
  size: '48px',
};

const Root = styled.header<{ $borderBottom: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;
  margin: 0;
  min-height: 76px;
  ${({ $borderBottom }) =>
    $borderBottom &&
    css`
      border-bottom: 1px solid ${color.border};
    `}
  background-color: ${color.bg};
  color: ${color.text};
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 16px;
  padding-bottom: 16px;

  @media (min-width: 550px) {
    padding-left: 24px;
    padding-right: 24px;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
`;

const RightColumn = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const Heading = styled.h1`
  ${legacyToolsTypographyCss.Heading6()}
  margin: 0;
`;

const Subtitle = styled.div`
  ${typographyCss.captionLabel}
  color: ${color.textSubdued};
`;

type Props = {
  children?: React.ReactNode;
  /**
   * Explicity set onDismiss to null to avoid mistakes forgetting to add it
   */
  onDismiss: React.MouseEventHandler<HTMLButtonElement> | null;
  subtitle?: React.ReactNode;
  borderBottom?: boolean;
  mobileOptimized?: boolean;
} & AccessabilityProps;

export const ModalHeader = ({
  borderBottom = true,
  children,
  onDismiss,
  subtitle,
  mobileOptimized,
  ...rest
}: Props) => {
  const f = useFormatMessage();

  return (
    <Root $borderBottom={borderBottom} {...rest}>
      <LeftColumn>
        <Heading>{children}</Heading>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </LeftColumn>

      <RightColumn>
        {onDismiss && (
          <IconButton
            aria-label={f('common.close_dialog')}
            size="small"
            background="solid"
            variant="neutral"
            alignment="boundingBox"
            onClick={onDismiss}
            data-test-id="dismiss-button"
            styleParams={mobileOptimized ? styleParams : undefined}
            icon={icons.cross}
          />
        )}
      </RightColumn>
    </Root>
  );
};
